import React from 'react';
import type { Root } from 'react-dom/client';
import { createRoot } from 'react-dom/client';
import { getComponent } from './registry';
import { COMPONENT_ATTRIBUTE, PROP_PREFIX } from './constants';
import ShortcodeProvider from '../provider/ShortcodeProvider';

function parsePropsFromAttributes(element: Element): Record<string, unknown> {
  const props: Record<string, unknown> = {};
  const attributes = (element as HTMLElement).attributes;
  for (let i = 0; i < attributes.length; i++) {
    const attr = attributes[i];
    if (attr.name.startsWith(PROP_PREFIX)) {
      const propName = attr.name.substring(PROP_PREFIX.length).replace(/-(\w)/g, (_, char) => char.toUpperCase());

      try {
        props[propName] = JSON.parse(attr.value);
      } catch (e) {
        props[propName] = attr.value;
      }
    }
  }
  return props;
}

interface MountInfo {
  root: Root;
  componentName: string;
}
const mountedComponents = new Map<Element, MountInfo>();
let observer: MutationObserver | null = null;

export function mountComponents(): void {
  const selector = `[${COMPONENT_ATTRIBUTE}]`;
  const elements = document.querySelectorAll<HTMLElement>(selector);

  elements.forEach((element) => {
    if (mountedComponents.has(element)) {
      return;
    }

    const reactComponentKey = element.dataset.reactShortcode;

    if (!reactComponentKey) {
      console.warn(`Element found but missing '${COMPONENT_ATTRIBUTE}' value.`, element);
      return;
    }

    const Component = getComponent(reactComponentKey);

    if (!Component) {
      console.warn(`Component "${reactComponentKey}" not found in registry. Make sure it's registered.`);
      return;
    }

    const props = parsePropsFromAttributes(element);

    // Create React root directly on the element
    const reactRoot = createRoot(element);

    reactRoot.render(
      <React.StrictMode>
        <ShortcodeProvider>
          <Component {...props} />
        </ShortcodeProvider>
      </React.StrictMode>,
    );

    mountedComponents.set(element, { root: reactRoot, componentName: reactComponentKey });
  });

  observeDOMChanges(selector);
}

/**
 * Unmounts a React component previously mounted by mountComponents.
 * @param element - The DOM element the component was mounted on.
 */
function unmountComponent(element: Element) {
  const mountInfo = mountedComponents.get(element);
  if (mountInfo) {
    mountInfo.root.unmount();
    mountedComponents.delete(element);
  }
}

/**
 * Sets up a MutationObserver to automatically unmount components when their
 * host elements are removed from the DOM.
 * @param selector - The CSS selector used to find component host elements.
 */
function observeDOMChanges(selector: string): void {
  if (observer) {
    observer.disconnect();
  }

  observer = new MutationObserver((mutationsList) => {
    for (const mutation of mutationsList) {
      if (mutation.type === 'childList') {
        mutation.removedNodes.forEach((node) => {
          if (node instanceof Element) {
            if (node.matches(selector)) {
              unmountComponent(node);
            }
            node.querySelectorAll<Element>(selector).forEach(unmountComponent);
          }
        });
        mutation.addedNodes.forEach((node) => {
          if (node instanceof Element) {
            const elementsToMount: Element[] = [];
            if (node.matches(selector)) {
              elementsToMount.push(node);
            }
            elementsToMount.push(...Array.from(node.querySelectorAll(selector)));

            elementsToMount.forEach((el) => {
              if (!mountedComponents.has(el)) {
                mountComponents();
              }
            });
          }
        });
      }
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

export function unmountAllComponents(): void {
  if (observer) {
    observer.disconnect();
    observer = null;
  }
  mountedComponents.forEach((_, element) => {
    unmountComponent(element);
  });
  console.info('Unmounted all Skin Seoul components.');
}
